import React, { useState } from "react";
import { UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import SpeakerAvatar from "./SpeakerAvatar";
import { photoSrc } from "../lib/trainerProfile";

/**
 * Who teaches a cohort, on its card (16.10.2026): a photo, the name, a
 * one-line title and, when there is a description, "Despre formator". The
 * API sends `trainer` only for a trainer who agreed and whose profile the
 * team switched on; without it, nothing is drawn. Texts validated in
 * docs/texte-formator-pe-site-pr3-2026-10-16.md.
 *
 * `compact`: the small line in the list of cohorts at checkout — inside a
 * button, so nothing in it can be clicked.
 */
export default function TrainerLine({ trainer, pending = false, label = "Formator:", compact = false, testid,
  className = "" }) {
  const [open, setOpen] = useState(false);
  // Nobody chosen yet for the cohort (T-1, 16.10.2026). The API says so only
  // then: a trainer who is chosen but not on the site is not "to be confirmed".
  if (!trainer?.name && pending) {
    return (
      <span className={`flex items-center gap-2 ${compact ? "text-xs" : "text-sm"} text-[#6B7280] ${className}`}
        data-testid={testid ? `${testid}-pending` : undefined}>
        <span className={`grid shrink-0 place-items-center rounded-full bg-[#F0E7F4] text-[#965EA7] ${compact ? "h-6 w-6" : "h-9 w-9"}`}
          aria-hidden="true">
          <UserRound size={compact ? 13 : 17} />
        </span>
        <span>{label} <span className="italic">urmează să fie confirmat</span></span>
      </span>
    );
  }
  if (!trainer?.name) return null;
  const src = photoSrc(trainer.photo_url);

  if (compact) {
    return (
      <span className={`flex items-center gap-2 text-xs text-[#6B7280] ${className}`} data-testid={testid}>
        <SpeakerAvatar name={trainer.name} photoUrl={src} size={24} />
        <span>{label} <span className="font-semibold text-[#373A45]">{trainer.name}</span></span>
      </span>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid={testid}>
      <SpeakerAvatar name={trainer.name} photoUrl={src} size={36} />
      <div className="min-w-0 text-sm leading-snug">
        <div className="text-[#373A45]">{label} <strong className="font-semibold">{trainer.name}</strong></div>
        {(trainer.title || trainer.bio) && (
          <div className="text-xs text-[#6B7280]">
            {trainer.title}
            {trainer.title && trainer.bio && " · "}
            {trainer.bio && (
              <button type="button" onClick={() => setOpen(true)}
                className="font-semibold text-[#7A4986] underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#965EA7]/60 rounded"
                data-testid={testid ? `${testid}-about` : undefined}>
                Despre formator
              </button>
            )}
          </div>
        )}
      </div>
      {trainer.bio && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md" data-testid={testid ? `${testid}-dialog` : undefined}>
            <div className="flex flex-col items-center text-center">
              <SpeakerAvatar name={trainer.name} photoUrl={src} size={120} />
              <DialogTitle className="mt-4 font-display text-xl font-bold text-[#373A45]">{trainer.name}</DialogTitle>
              {trainer.title && <p className="mt-1 text-sm text-[#6B7280]">{trainer.title}</p>}
            </div>
            <DialogDescription className="mt-2 whitespace-pre-line text-left text-sm leading-relaxed text-[#373A45]">
              {trainer.bio}
            </DialogDescription>
            <div className="mt-2 flex justify-center">
              <button type="button" onClick={() => setOpen(false)}
                className="rounded-full bg-[#965EA7] px-6 py-2 text-sm font-semibold text-white hover:bg-[#7A4986]">
                Închide
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
